import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import React from "react";

const Contact: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [subscribe, setSubscribe] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    message: ""
  });

  const validateName = () => {
    if (!name.trim()) {
      setErrors(prev => ({ ...prev, name: "Name is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, name: "" }));
    return true;
  };

  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setErrors(prev => ({ ...prev, email: "Email is required" }));
      return false;
    }
    if (!emailRegex.test(email)) {
      setErrors(prev => ({ ...prev, email: "Please enter a valid email" }));
      return false;
    }
    setErrors(prev => ({ ...prev, email: "" }));
    return true;
  };

  const validateMessage = () => {
    if (!message.trim()) {
      setErrors(prev => ({ ...prev, message: "Message is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, message: "" }));
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!validateName() || !validateEmail() || !validateMessage()) {
      return
    }
    try {
      await fetch('https://usercom.babbage.systems/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'feedback',
          name,
          email,
          feedback: message,
          newsletterSubscribe: subscribe ? 1 : 0
        })
      })
      setSubmitted(true)
    } catch (e: unknown) {
      window.alert(e instanceof Error ? e.message : 'An error occurred')
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center rounded-full border border-border px-4 py-2 text-sm mb-6 bg-transparent">
              <span className="text-foreground">Contact</span>
              <div className="mx-1 h-1 w-1 rounded-full bg-muted"></div>
              <span className="text-muted-foreground">Get in Touch</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Let's <span className="text-blue-500">Connect</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Have questions about our products or services? We're here to help.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="space-y-8"
            >
              <div className="bg-card rounded-xl p-8 border border-border">
                <h2 className="text-2xl font-bold text-foreground mb-6">Send us a Message</h2>
                {submitted ? (
                  <div className="text-center py-8">
                    <h3 className="text-xl font-bold text-green-500 mb-4">Thank You!</h3>
                    <p className="text-muted-foreground">
                      Your message has been sent successfully. We'll get back to you soon.
                    </p>
                  </div>
                ) : (
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                          Name
                        </label>
                        <Input
                          id="name"
                          placeholder="Your name"
                          className={`bg-background border-border text-foreground ${errors.name ? "border-red-500" : ""}`}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          onBlur={validateName}
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                          Email
                        </label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          className={`bg-background border-border text-foreground ${errors.email ? "border-red-500" : ""}`}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onBlur={validateEmail}
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                          Message
                        </label>
                        <Textarea
                          id="message"
                          placeholder="Your message..."
                          className={`bg-background border-border text-foreground min-h-[150px] ${errors.message ? "border-red-500" : ""}`}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onBlur={validateMessage}
                        />
                        {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="subscribe" 
                          checked={subscribe} 
                          onCheckedChange={(checked) => setSubscribe(checked === true)}
                        />
                        <label
                          htmlFor="subscribe"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Subscribe to our newsletter
                        </label>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      Send Message
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="bg-card rounded-xl p-8 border border-border">
                <h2 className="text-2xl font-bold text-foreground mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Mail className="h-6 w-6 text-blue-400 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Email</h3>
                      <p className="text-muted-foreground">support@projectbabbage.com</p>
                      <p className="text-sm text-muted-foreground mt-1">We aim to respond within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="h-6 w-6 text-blue-400 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Location</h3>
                      <p className="text-muted-foreground">Oregon, United States</p>
                      <p className="text-sm text-muted-foreground mt-1">Serving clients worldwide</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl p-8 border border-border">
                <h2 className="text-2xl font-bold text-foreground mb-6">Schedule a Call</h2>
                <p className="text-muted-foreground mb-6">
                  Want to discuss your project in detail? Schedule a call with our team.
                </p>
                <Button
                  variant="outline"
                  className="w-full border-border text-foreground hover:bg-muted"
                  onClick={() => window.open('https://calendly.com/projectbabbage', '_blank')}
                >
                  Book a Meeting
                  <MessageSquare className="ml-2 h-4 w-4" />
                </Button>
              </div>

              <div className="bg-gradient-to-r from-blue-500/10 to-primary/10 rounded-xl p-8 border border-border">
                <h2 className="text-xl font-bold text-foreground mb-4">Join Our Community</h2>
                <p className="text-muted-foreground mb-6">
                  Connect with other developers and stay updated with the latest news.
                </p>
                <Button
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  onClick={() => window.open('https://join.slack.com/t/projectbabbage/shared_invite/zt-29gkwwaoj-yFqgWEC4YT_Sq7eH5Expsw', '_blank')}
                >
                  Join Slack
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;